import { useParams, Link, Navigate } from "react-router-dom";
import SEOHead, { breadcrumbSchema, faqSchema, organizationSchema } from "@/components/SEOHead";
import MarketingHeader from "@/components/MarketingHeader";
import MarketingFooter from "@/components/MarketingFooter";
import FaqBlock, { CtaBlock } from "@/components/FaqBlock";
import { COMPETITORS, SITE } from "@/lib/seoData";
import { Check, X } from "lucide-react";

export default function ComparePage() {
  const { competitor } = useParams();
  const data = COMPETITORS.find((c) => c.slug === competitor);
  if (!data) return <Navigate to="/" replace />;

  const title = `BreakItDown vs ${data.name} — Which Task Manager Wins in 2026?`;
  const description = `Honest BreakItDown vs ${data.name} comparison. AI task breakdown, focus timer, pricing, and best-for use cases — side by side.`;
  const path = `/compare/${data.slug}`;

  const faqs = [
    {
      q: `Is BreakItDown better than ${data.name}?`,
      a: `BreakItDown is purpose-built around AI task breakdown and focus, with a tiny learning curve. ${data.name} is a more general-purpose tool. If you want to be productive in 30 seconds rather than 30 minutes, BreakItDown is the better pick.`,
    },
    {
      q: `Is BreakItDown free?`,
      a: `Yes — BreakItDown is 100% free, including AI breakdowns and install-to-home-screen. ${data.name} typically has free and paid tiers.`,
    },
    {
      q: `Does BreakItDown have AI like ${data.name}?`,
      a: `Yes. BreakItDown uses Claude Sonnet 4.5 to break any task into 4–6 actionable subtasks. The AI is included free.`,
    },
    {
      q: `Can I import my ${data.name} tasks into BreakItDown?`,
      a: `Direct import isn't supported today — but BreakItDown is intentionally lightweight, so most users start fresh by typing one big task and letting the AI break it down.`,
    },
    {
      q: `What's the best alternative to ${data.name}?`,
      a: `BreakItDown is one of the best free alternatives to ${data.name} if your goal is AI-assisted task planning rather than full project management.`,
    },
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SEOHead
        title={title}
        description={description}
        path={path}
        schemas={[
          organizationSchema(),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Compare", path: "/compare/clickup" },
            { name: `vs ${data.name}`, path },
          ]),
          faqSchema(faqs),
        ]}
      />
      <MarketingHeader />

      <main className="flex-1 max-w-4xl mx-auto px-6 md:px-10 py-12 md:py-16 w-full">
        <nav className="text-xs text-zinc-500 mb-6">
          <Link to="/" className="hover:text-zinc-900">Home</Link>
          {" / "}
          <span className="text-zinc-700">vs {data.name}</span>
        </nav>

        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-3">
          BreakItDown vs {data.name}
        </p>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-900 leading-tight mb-4">
          {data.name} is great for {data.tagline.toLowerCase()}.<br />
          <span className="text-zinc-400">BreakItDown is built for getting it done.</span>
        </h1>
        <p className="text-base text-zinc-600 leading-relaxed mt-4 max-w-3xl">{data.positioning}</p>

        {/* Quick answer for AI extraction */}
        <div className="border-l-2 border-[#002FA7] bg-zinc-50 px-5 py-4 my-10">
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-2">Quick answer</p>
          <p className="text-sm text-zinc-800 leading-relaxed">
            <strong>BreakItDown</strong> wins for solo focus, ADHD productivity, and small teams that
            want AI task breakdown with no setup. <strong>{data.name}</strong> wins for teams that
            need full project management with timelines, dashboards, and integrations.
          </p>
        </div>

        {/* Differences */}
        <h2 className="text-2xl font-semibold text-zinc-900 mt-12 mb-4">Key differences</h2>
        <ul className="space-y-3">
          {data.differences.map((d, i) => (
            <li key={i} className="flex items-start gap-3 text-sm text-zinc-700 leading-relaxed">
              <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" strokeWidth={2.5} />
              {d}
            </li>
          ))}
        </ul>

        {/* Table */}
        <h2 className="text-2xl font-semibold text-zinc-900 mt-12 mb-4">Feature comparison</h2>
        <div className="border border-zinc-200 overflow-x-auto -mx-6 sm:mx-0">
          <table className="w-full text-sm min-w-[480px]">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50">
                <th className="text-left p-3 text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">Feature</th>
                <th className="text-left p-3 text-xs uppercase tracking-[0.1em] font-semibold text-zinc-900">BreakItDown</th>
                <th className="text-left p-3 text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500">{data.name}</th>
              </tr>
            </thead>
            <tbody>
              {data.table.map((row, i) => (
                <tr key={i} className="border-b border-zinc-200 last:border-0">
                  <td className="p-3 text-zinc-700 font-medium">{row[0]}</td>
                  <td className="p-3 text-zinc-900">{renderCell(row[1])}</td>
                  <td className="p-3 text-zinc-600">{renderCell(row[2])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h2 className="text-2xl font-semibold text-zinc-900 mt-12 mb-4">Who should pick {data.name}?</h2>
        <p className="text-sm text-zinc-700 leading-relaxed">
          Pick {data.name} if you already work in a team that uses it, or if you need its specific
          coordination features. For everything else — and especially if you find {data.name} too
          much to set up — try <Link to="/" className="text-[#002FA7] underline">BreakItDown</Link>.
        </p>

        <h2 className="text-2xl font-semibold text-zinc-900 mt-12 mb-4">Other comparisons</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {COMPETITORS.filter((c) => c.slug !== data.slug).map((c) => (
            <Link
              key={c.slug}
              to={`/compare/${c.slug}`}
              className="border border-zinc-200 px-3 py-2 text-sm hover:border-zinc-900 transition-colors"
            >
              vs {c.name}
            </Link>
          ))}
        </div>

        <FaqBlock faqs={faqs} />
        <CtaBlock label={`Try BreakItDown instead of ${data.name}`} />
      </main>

      <MarketingFooter />
    </div>
  );
}

function renderCell(value) {
  if (value === "No") return <span className="inline-flex items-center gap-1 text-red-700"><X className="w-3.5 h-3.5" strokeWidth={2} />No</span>;
  if (value === "Yes") return <span className="inline-flex items-center gap-1 text-emerald-700"><Check className="w-3.5 h-3.5" strokeWidth={2.5} />Yes</span>;
  return value;
}
